<template>
  <div class="feedback-container">
    <!-- Floating Action Button -->
    <div 
      class="fab" 
      :class="{ 'fab--expanded': isExpanded }"
      @click="toggleExpanded"
    >
      <div class="fab__icon">
        <span v-if="!isExpanded">💬</span>
        <span v-else>✕</span>
      </div>
    </div>

    <!-- Feedback Form Overlay -->
    <div 
      v-if="isExpanded" 
      class="feedback-overlay"
      @click="closeForm"
    >
      <div 
        class="feedback-form"
        @click.stop
      >
        <div class="feedback-form__header">
          <h3>Send Feedback</h3>
          <button class="feedback-form__close" @click="closeForm">✕</button>
        </div>

        <form @submit.prevent="submitFeedback" class="feedback-form__content">
          <div class="form-group">
            <label for="name">Name (optional)</label>
            <input 
              id="name"
              v-model="formData.name"
              type="text"
              placeholder="Your name"
              class="form-input"
            >
          </div>

          <div class="form-group">
            <label for="email">Email (optional)</label>
            <input 
              id="email"
              v-model="formData.email"
              type="email"
              placeholder="your.email@example.com"
              class="form-input"
            >
          </div>

          <div class="form-group">
            <label for="type">Feedback Type</label>
            <select 
              id="type"
              v-model="formData.type"
              class="form-select"
            >
              <option value="general">General Feedback</option>
              <option value="bug">Bug Report</option>
              <option value="feature">Feature Request</option>
              <option value="support">Support Request</option>
            </select>
          </div>

          <div class="form-group">
            <label for="message">Message *</label>
            <textarea 
              id="message"
              v-model="formData.message"
              placeholder="Tell us what you think..."
              class="form-textarea"
              rows="4"
              required
            ></textarea>
          </div>

          <div class="feedback-form__actions">
            <button 
              type="button" 
              class="btn btn--secondary"
              @click="closeForm"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              class="btn btn--primary"
              :disabled="isSubmitting"
            >
              {{ isSubmitting ? 'Sending...' : 'Send Feedback' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";

interface FeedbackFormData {
  name: string;
  email: string;
  message: string;
  type: string;
}

@Options({
  name: 'FeedbackButton'
})
export default class FeedbackButton extends Vue {
  isExpanded = false;
  isSubmitting = false;
  formData: FeedbackFormData = {
    name: '',
    email: '',
    message: '',
    type: 'general'
  };

  toggleExpanded() {
    this.isExpanded = !this.isExpanded;
  }

  closeForm() {
    this.isExpanded = false;
    this.resetForm();
  }

  resetForm() {
    this.formData = {
      name: '',
      email: '',
      message: '',
      type: 'general'
    };
  }

  async submitFeedback() {
    if (!this.formData.message) {
      return;
    }

    this.isSubmitting = true;

    try {
      // Send feedback via API
      const response = await fetch('/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: this.formData.name || null,
          email: this.formData.email || null,
          type: this.formData.type,
          message: this.formData.message
        })
      });

      if (!response.ok) {
        let errorMessage = 'Failed to send feedback';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (jsonError) {
          // If response is not JSON, try to get text
          try {
            const textError = await response.text();
            errorMessage = textError || errorMessage;
          } catch (textError) {
            // If all else fails, use the status text
            errorMessage = response.statusText || errorMessage;
          }
        }
        throw new Error(errorMessage);
      }

      // Handle success response
      try {
        const responseData = await response.json();
        console.log('Feedback sent successfully:', responseData);
      } catch (jsonError) {
        // If response is not JSON, that's okay for success case
        console.log('Feedback sent successfully (non-JSON response)');
      }
      
      // Show success message
      this.$emit('feedback-sent', { success: true });
      
      // Close form and reset
      this.closeForm();
      
      // Show success notification
      this.showNotification('Feedback sent successfully!', 'success');
      
    } catch (error) {
      console.error('Error sending feedback:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to send feedback. Please try again.';
      this.showNotification(errorMessage, 'error');
    } finally {
      this.isSubmitting = false;
    }
  }

  showNotification(message: string, type: 'success' | 'error') {
    // Simple notification - you can enhance this with a proper notification system
    alert(message);
  }
}
</script>

<style scoped lang="scss">
.feedback-container {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 1000;
}

.fab {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 1001;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  }

  &--expanded {
    background: #dc3545;
  }
}

.fab__icon {
  color: white;
  font-size: 1.5rem;
  font-weight: bold;
}

.feedback-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease-out;
}

.feedback-form {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease-out;
}

.feedback-form__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid #e9ecef;

  h3 {
    margin: 0;
    color: #2c3e50;
    font-size: 1.25rem;
    font-weight: 600;
  }
}

.feedback-form__close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0.25rem;
  border-radius: 4px;
  transition: color 0.2s;

  &:hover {
    color: #dc3545;
  }
}

.feedback-form__content {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.25rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #495057;
    font-size: 0.875rem;
  }
}

.form-input,
.form-textarea,
.form-select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 0.875rem;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
}

.feedback-form__actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &--primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  &--secondary {
    background: #6c757d;
    color: white;

    &:hover {
      background: #5a6268;
    }
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .feedback-container {
    bottom: 1rem;
    right: 1rem;
  }

  .fab {
    width: 50px;
    height: 50px;
  }

  .fab__icon {
    font-size: 1.25rem;
  }

  .feedback-form {
    width: 95%;
    margin: 1rem;
  }

  .feedback-form__actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }
}
</style> 